import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export const ValueSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useTranslation();

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-background relative overflow-hidden">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-4">
            {t('value.title')}
          </h2>
          <p className="text-muted-foreground">
            {t('value.subtitle')}
          </p>
        </motion.div>

        {/* Value Visualization */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative max-w-4xl mx-auto"
        >
          <svg viewBox="0 0 800 400" className="w-full h-auto">
            <defs>
              {/* Left Stream Gradient */}
              <linearGradient id="leftStream" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity="0.8" />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.8" />
              </linearGradient>
              
              {/* Right Stream Gradient */}
              <linearGradient id="rightStream" x1="100%" y1="0%" x2="0%" y2="0%">
                <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity="0.8" />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.8" />
              </linearGradient>

              {/* Center Glow */}
              <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.6" />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
              </radialGradient>

              {/* Particle Filter */}
              <filter id="glow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>

            {/* Left Side - Business Needs */}
            <motion.g
              initial={{ opacity: 0, x: -30 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <text x="50" y="80" fill="hsl(var(--foreground))" fontSize="14" fontWeight="600">
                {t('value.businessNeed')}
              </text>
              
              {/* Flowing particles from left */}
              {[...Array(8)].map((_, i) => (
                <motion.circle
                  key={`left-${i}`}
                  cx={80 + i * 30}
                  cy={180 + (i % 3) * 30 - 15}
                  r={4 + (i % 3)}
                  fill="url(#leftStream)"
                  filter="url(#glow)"
                  initial={{ opacity: 0.3, x: -50 }}
                  animate={isInView ? { 
                    opacity: [0.3, 0.8, 0.3],
                    x: [0, 100, 200]
                  } : {}}
                  transition={{ 
                    duration: 3 + i * 0.5, 
                    repeat: Infinity,
                    delay: i * 0.3
                  }}
                />
              ))}
            </motion.g>

            {/* Right Side - Retiree Expertise */}
            <motion.g
              initial={{ opacity: 0, x: 30 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <text x="600" y="80" fill="hsl(var(--foreground))" fontSize="14" fontWeight="600" textAnchor="end">
                {t('value.retireeExpertise')}
              </text>
              
              {/* Flowing particles from right */}
              {[...Array(8)].map((_, i) => (
                <motion.circle
                  key={`right-${i}`}
                  cx={720 - i * 30}
                  cy={180 + (i % 3) * 30 - 15}
                  r={4 + (i % 3)}
                  fill="url(#rightStream)"
                  filter="url(#glow)"
                  initial={{ opacity: 0.3, x: 50 }}
                  animate={isInView ? { 
                    opacity: [0.3, 0.8, 0.3],
                    x: [0, -100, -200]
                  } : {}}
                  transition={{ 
                    duration: 3 + i * 0.5, 
                    repeat: Infinity,
                    delay: i * 0.3
                  }}
                />
              ))}
            </motion.g>

            {/* Center - Value Created */}
            <motion.g
              initial={{ scale: 0, opacity: 0 }}
              animate={isInView ? { scale: 1, opacity: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.6, type: "spring" }}
            >
              {/* Glow Effect */}
              <circle cx="400" cy="200" r="80" fill="url(#centerGlow)" />
              
              {/* Main Circle */}
              <circle cx="400" cy="200" r="50" fill="hsl(var(--primary))" />
              
              {/* Text */}
              <text x="400" y="195" textAnchor="middle" fill="hsl(var(--primary-foreground))" fontSize="12" fontWeight="600">
                {t('value.valueCreated').split(' ')[0]}
              </text>
              <text x="400" y="212" textAnchor="middle" fill="hsl(var(--primary-foreground))" fontSize="12" fontWeight="600">
                {t('value.valueCreated').split(' ')[1]}
              </text>
            </motion.g>

            {/* Connecting Arrows */}
            <motion.g
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 0.5 } : {}}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <path
                d="M 150 200 Q 275 200 340 200"
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="2"
                strokeDasharray="5,5"
              />
              <path
                d="M 460 200 Q 525 200 650 200"
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="2"
                strokeDasharray="5,5"
              />
            </motion.g>

            {/* Bottom Labels */}
            <motion.g
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 1 }}
            >
              <text x="120" y="350" textAnchor="middle" fill="hsl(var(--muted-foreground))" fontSize="11">
                {t('value.businessLabels')}
              </text>
              <text x="680" y="350" textAnchor="middle" fill="hsl(var(--muted-foreground))" fontSize="11">
                {t('value.retireeLabels')}
              </text>
              <text x="400" y="320" textAnchor="middle" fill="hsl(var(--primary))" fontSize="13" fontWeight="600">
                sfretire.com
              </text>
            </motion.g>
          </svg>
        </motion.div>
      </div>
    </section>
  );
};
